import { observable, action, autorun } from 'mobx';

import agent from '../agent';

class DataStore {

  @observable loadingData;
  @observable loadingMap = false;
  @observable loadingNearestPoi = false;
  @observable loadingExportCurves = false;
  @observable loadingExportMaps = false;

  @observable circlesInfo = [];
  @observable faultsInfo = [];
  @observable top5FaultNames = [];
  @observable curveData = {};
  @observable markerLoc = null;
  @observable closestPoi = null;
  @observable lineLimits = [];

  @observable dataSubmitError;
  @observable getNearestPoiError;
  @observable exportCurvesError;
  @observable exportMapsError;

  @observable currentMaptype = 'hazard'; // hazard , probability
  @observable currentMihPoe = '2475';

  @observable isModalActive = false;

  @action
  submit({ files }) {
    this.loadingData = true;
    this.dataSubmitError = undefined;

    return agent.Data.submit(files)
      .catch(
        action(err => {
          const responseBody = err.response.body;
          this.dataSubmitError =
            {
              type: responseBody.content,
              details: responseBody.details || null,
            } || err;
        })
      )
      .finally(
        action(() => {
          this.loadingData = false;
        })
      );
  }

  @action
  setCurrentMaptype(maptype) {
    this.currentMaptype = maptype;
  }

  @action
  setCurrentMihPoe(mihPoe) {
    this.currentMihPoe = mihPoe;
  }

  @action
  setCirclesInfo(circleColLoc) {
    this.circlesInfo = circleColLoc;
  }

  @action
  setFaultsInfo(fi) {
    this.faultsInfo = fi;
  }

  @action
  getNearestPoi(latlng) {
    this.loadingNearestPoi = true;
    const stringLatLng = `${latlng[0]},${latlng[1]}`;
    return agent.Data.getNearestPoi(encodeURIComponent(stringLatLng))
      .catch(
        action(err => {
          const responseBody = err.response.body;
          this.getNearestPoiError =
            {
              type: responseBody.content,
              details: responseBody.details || null,
            } || err;
        })
      )
      .finally(
        action(() => {
          this.loadingNearestPoi = false;
        })
      );
  }

  @action
  setCurveData(result) {
    if (result[0]) {
      this.isModalActive = true;

      this.markerLoc = [result[1], result[2]];

      const fullData = result[0];

      const groupedData = {};

      let minx = Infinity;
      let miny = Infinity;
      let maxx = -Infinity;
      let maxy = -Infinity;
      fullData.forEach(({ mih, poe, fault }) => {
        if (mih < minx) minx = mih;
        if (poe < miny) miny = poe;
        if (mih > maxx) maxx = mih;
        if (poe > maxy) maxy = poe;
        if (!fault) {
          if (!groupedData.All) {
            groupedData.All = { rows: [[mih, poe]] };
          } else {
            groupedData.All.rows.push([mih, poe]);
          }
          return;
        }
        if (!groupedData[fault.name]) {
          groupedData[fault.name] = { rows: [[mih, poe]], faultInfo: fault };
        } else {
          groupedData[fault.name].rows.push([mih, poe]);
        }
      });

      this.curveData = groupedData;
      this.closestPoi = result[3];
      this.lineLimits = [minx, miny, maxx, maxy];
      this.top5FaultNames = Object.keys(groupedData).filter(e => e !== 'All');
    }
  }

  @action
  closeCurveModal() {
    this.isModalActive = false;
  }

  @action
  exportCurves(j) {
    this.loadingExportCurves = true;
    return agent.Data.exportCurves()
      .then(res => {
        const blob = new Blob([res], { type: 'octet/stream' });
        const url = URL.createObjectURL(blob);

        const tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.setAttribute('download', `${this.currentDatasetName}-hazard_curves.zip`);
        tempLink.click();
      })
      .finally(
        action(() => {
          this.loadingExportCurves = false;
        })
      );
  }

  @action
  exportMaps() {
    this.loadingExportMaps = true;
    return agent.Data.exportMaps()
      .then(res => {
        const blob = new Blob([res], { type: 'octet/stream' });
        const url = URL.createObjectURL(blob);

        const tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.setAttribute('download', `${this.currentDatasetName}-maps.zip`);
        tempLink.click();
      })
      .finally(
        action(() => {
          this.loadingExportMaps = false;
        })
      );
  }

  constructor() {
    autorun(
      () => {

        this.loadingMap = true;
        agent.Data.getMap(`${this.currentMaptype}-${this.currentMihPoe}`)
          .then(
            action(response => {
              const circleColLoc = response.content; // [Circle Color, Circle Location]
              this.setCirclesInfo(circleColLoc);
            })
          )
          .then(() => agent.Data.getFaults())
          .then(
            action(response => {
              const faults = response.content;
              this.setFaultsInfo(faults);
            })
          )
          .finally(
            action(() => {
              this.loadingMap = false;
            })
          );
      },
      {
        delay: 100,
      }
    );
  }
}

export default new DataStore();



// WEBPACK FOOTER //
// ./src/store/dataStore.js